// src/prf/prfIntervalHistory.js
// "How often would this have paid, and what did it return per premium dollar?"
//
// ═══════════════════════════════════════════════════════════════════════════
// THE DENOMINATOR, DEFINED ONCE, BEFORE ANY OF IT WAS BUILT
// ═══════════════════════════════════════════════════════════════════════════
//
// A loss ratio whose denominator changes between screens is worse than no loss
// ratio, because both readings look authoritative. So it is written down here
// and asserted in check-prf-interval-history, not left to the call site.
//
// THE DENOMINATOR IS PRODUCER PREMIUM — what the grower actually pays, after
// RMA's subsidy. Two reasons, and the first is a source rather than a
// preference:
//
//   1. It is the convention the AIP already prints. Rain and Hail's Coverage
//      Analyzer for grid 27509 shows Level 85 "Premium 42,313 / Indemnity
//      41,732 / Loss Ratio 99%", and 42,313 is 16 full years x $2,489 producer
//      premium plus two partial years — NOT the $5,531 total premium. Matching
//      their denominator means our number and theirs answer the same question.
//   2. It is the question a grower asks: what came back per dollar I paid.
//
// ⚠ THE INDUSTRY/RMA LOSS RATIO USES **TOTAL** PREMIUM AND IS A DIFFERENT
// NUMBER. At 85% the subsidy is 55%, so a producer-basis ratio is about 2.2x
// the program-basis one. 142% here and 64% in an RMA program report can
// describe the identical experience. Never mix them, and never leave the word
// "loss ratio" unqualified on a screen.
//
// ⚠ AND IT IS A **REPLAY**, NOT LOSS EXPERIENCE. Every year is priced with the
// SELECTED crop year's rates, county base value, coverage level and
// productivity factor — not with whatever was actually charged in 1998. So it
// answers "how would today's structure have behaved against this grid's
// rainfall record", which is the useful question, and it is NOT the historical
// performance of anybody's policy. Hence the name: Replay Loss Ratio.
//
// ═══════════════════════════════════════════════════════════════════════════
// TWO LEVELS, AND THEY ARE DELIBERATELY COMPUTED DIFFERENTLY
// ═══════════════════════════════════════════════════════════════════════════
//
// POLICY REPLAY LOSS RATIO — the elected structure, over window W:
//
//        sum over y in W of Indemnity_policy(y)
//        --------------------------------------
//            |W| x ProducerPremium_policy
//
//   Computed from the REAL replay (simulatePrf), so every dollar carries RMA's
//   own per-interval rounding. It answers "what would this policy have done".
//
// INTERVAL REPLAY LOSS RATIO — one interval, elected or not:
//
//   Kevin's framing: "had one dollar of premium exposure been allocated to this
//   interval, how much historical indemnity would it have returned?" Written
//   out, for an interval i carrying protection P:
//
//        Indemnity_i(y)      = P x paymentFactor(cov, index_i(y))
//        ProducerPremium_i   = P x rate_i x (1 - subsidy)
//
//        LR_i = sum_y [P x pf_i(y)]              mean_y[ pf_i(y) ]
//               -------------------------   =   -------------------
//               |W| x P x rate_i x (1-s)         rate_i x (1 - s)
//
//   **P CANCELS.** The interval ratio is therefore independent of the percent
//   of value, the acres, the share, the county base value AND the productivity
//   factor — which is exactly what makes intervals comparable to each other,
//   and what lets an UNELECTED interval be scored beside an elected one without
//   inventing an allocation for it. It depends on only three things: the
//   payment factors in the window, the interval's own base rate at the selected
//   coverage level, and the subsidy at that level.
//
// ⚠ THE INTERVAL RATIO IS COMPUTED UNROUNDED, ON PURPOSE, AND THAT IS THE ONE
// PLACE THIS FILE DEPARTS FROM THE ENGINE'S ROUNDING. Rounding to whole dollars
// would reintroduce a dependence on P — a $200 interval and a $200,000 interval
// would score differently on a metric whose whole purpose is that they do not.
// The POLICY ratio keeps the rounding, because there the dollars are the point.
//
// THE TWO ARE COHERENT, AND THE CHECKER PROVES IT: the policy ratio equals the
// premium-weighted mean of its elected intervals' ratios, since
// LR_policy = sum(Ind_i) / sum(PP_i) = sum(LR_i x PP_i) / sum(PP_i).
// If that identity ever breaks, one of the two definitions has drifted.
//
// ═══════════════════════════════════════════════════════════════════════════
// TRIGGERED vs PAID — two different counts, never merged
// ═══════════════════════════════════════════════════════════════════════════
//
//   INTERVAL TRIGGERED : index_i(y) < coverage level, for one interval.
//   POLICY PAID        : ANY elected interval produced an indemnity that year.
//
// A three-interval policy where two intervals trigger in one year is ONE
// policy-paying year, not two. Summing interval triggers to get a policy figure
// overstates it exactly as often as intervals overlap in a bad year — which is
// most bad years.

const { simulatePrf } = require("./prfQuote.js");
const { paymentFactor } = require("./prfIndexMethodology.js");

const LAB = ["JANFEB", "FEBMAR", "MARAPR", "APRMAY", "MAYJUN", "JUNJUL",
             "JULAUG", "AUGSEP", "SEPOCT", "OCTNOV", "NOVDEC"];

// The periods the screen offers. "all" is the whole record; a custom range is
// {from, to} inclusive.
const PERIODS = [10, 15, 20, 25, 30, "all"];

const mean = (a) => (a.length ? a.reduce((s, x) => s + x, 0) / a.length : null);

/**
 * Resolve the selected window to an explicit ascending year list.
 *
 * ⚠ "LAST 20 YEARS" MEANS THE LAST 20 YEARS OF THE RECORD, NOT OF THE CALENDAR.
 * RMA's published index history for a grid ends at the last SETTLED year, which
 * is behind today — measured 2026-08-25, grid 27509's CY2026 vintage runs
 * 1948..2024. Counting back from the current year would silently include years
 * the record cannot answer for and shorten the window without saying so.
 */
function windowYears(allYears, period, custom) {
  const ys = [...new Set((allYears || []).map(Number).filter(Number.isFinite))].sort((a, b) => a - b);
  if (!ys.length) return [];
  if (custom && Number.isFinite(custom.from) && Number.isFinite(custom.to)) {
    const lo = Math.min(custom.from, custom.to), hi = Math.max(custom.from, custom.to);
    return ys.filter((y) => y >= lo && y <= hi);
  }
  if (period === "all" || period == null) return ys;
  const n = Number(period);
  if (!Number.isFinite(n) || n <= 0) return ys;
  return ys.slice(Math.max(0, ys.length - n));
}

/**
 * Per-interval history over the selected window.
 *
 * @param {object} a
 * @param {object} a.series          { INTERVAL: { year: index } }
 * @param {number[]} a.years         the WINDOW, ascending
 * @param {object} a.allocation      { INTERVAL: percent } — for the `elected` flag only
 * @param {number} a.coverageLevel
 * @param {object} a.rates           { INTERVAL: baseRate } at this coverage level
 * @param {number} a.subsidyRate
 */
function intervalHistory(a) {
  const { series, years, allocation, coverageLevel: cov, rates, subsidyRate } = a;
  const rows = [];
  for (const iv of LAB) {
    const s = series?.[iv];
    const ys = (years || []).filter((y) => Number.isFinite(s?.[y]));
    const elected = Number(allocation?.[iv] || 0);
    if (!ys.length) {
      rows.push({ interval: iv, available: false, elected, n_years: 0 });
      continue;
    }
    const factors = ys.map((y) => paymentFactor(cov, s[y]));
    const hit = factors.filter((f) => f > 0);
    const rate = Number(rates?.[iv]);
    const rateOk = Number.isFinite(rate) && rate > 0;

    rows.push({
      interval: iv, available: true, elected,
      n_years: ys.length, first_year: ys[0], last_year: ys[ys.length - 1],
      // TRIGGERED — the count is primary; the frequency is derived from it.
      triggered: hit.length,
      trigger_freq: hit.length / ys.length,
      // How big it was WHEN it paid — a different question from how often, and
      // null rather than 0 when it never paid, because "never triggered" and
      // "triggered and paid nothing" are not the same statement.
      avg_pay_factor_when_triggered: hit.length ? mean(hit) : null,
      avg_pay_factor_all: mean(factors),
      max_pay_factor: Math.max(...factors),
      mean_index: mean(ys.map((y) => s[y])),
      // See the header: mean(pf) / (rate x (1 - subsidy)). Protection cancels,
      // so this is comparable across intervals and needs no allocation.
      replay_loss_ratio: rateOk ? mean(factors) / (rate * (1 - subsidyRate)) : null,
      // Named absence — an interval RMA does not rate here cannot be scored, and
      // a null must never render as a zero return.
      loss_ratio_unavailable: rateOk ? null : "no published rate at this coverage level",
      base_rate: rateOk ? rate : null,
    });
  }
  return { intervals: rows, coverage_level: cov, subsidy_rate: subsidyRate, years: years || [] };
}

/**
 * The elected structure's own record over the same window — from the REAL
 * replay, so the dollars carry RMA's rounding.
 *
 * Returns null when the election cannot be priced; a summary strip that
 * silently disappears is better than one showing zeros, and the caller is
 * expected to say why.
 */
function policyHistory(a) {
  const { series, years, allocation, coverageLevel, rates,
          countyBaseValue, productivityFactor, acres, share = 1, rules } = a;
  if (!years?.length) return null;
  let sim;
  try {
    sim = simulatePrf({
      countyBaseValue, coverageLevel, productivityFactor, acres, share,
      allocation, rates, rules: rules || undefined, series, years,
    });
  } catch (e) {
    return { unavailable: String(e.message || "").replace(/^prfQuote:\s*/, "") };
  }
  if (!sim.summary) return { unavailable: "no complete years in this window" };

  const n = sim.years;
  const totalInd = sim.summary.total_indemnity;
  const totalPP = sim.summary.total_producer_premium;
  return {
    years: n,
    // ⚠ `skipped` IS REPORTED, NEVER SWALLOWED. simulatePrf omits a year whose
    // elected intervals are not all present, because a partial year would take
    // a full premium against an incomplete indemnity. A window that quietly
    // scored 17 of 20 years reads as 20.
    skipped: sim.skipped,
    years_paid: sim.summary.years_paid,
    payout_freq: sim.summary.payout_freq,
    total_indemnity: totalInd,
    total_producer_premium: totalPP,
    avg_premium: totalPP / n,
    avg_indemnity: totalInd / n,
    avg_net: (totalInd - totalPP) / n,
    replay_loss_ratio: totalPP ? totalInd / totalPP : null,
    largest_indemnity: sim.rows.reduce((m, r) => Math.max(m, r.indemnity || 0), 0),
    longest_dry_spell: sim.summary.longest_dry_spell,
    best: sim.summary.best, worst: sim.summary.worst,
    rows: sim.rows,
  };
}

// ── LOSS RATIO FOR ONE LADDER ROW, OVER A CHOSEN WINDOW ──────────────────────
//
// "If I had had this the last 10 years, what was my loss ratio?" — Kevin, 2026-09-02.
//
// ⚠ IT IS A REPLAY, NOT A HISTORY, AND THE TWO ARE DIFFERENT CLAIMS. The INDEX is
// RMA's published record; the PREMIUM and the PROTECTION are TODAY'S quote held
// constant across every year. So this answers "this election, at this year's rates,
// against what the rain actually did" — it is NOT what the grower's own loss ratio
// was, which would need the premiums and elections they actually held each year and
// is not in anything we hold. Every caller must say which of the two it is showing;
// `basis` is on the result so the label cannot drift from the arithmetic.
//
// The denominator is the PRODUCER premium, matching `policyHistory.replay_loss_ratio`
// above — one convention, so the two tabs cannot disagree about the same farm. The
// gross-premium ratio (RMA's own actuarial definition, always the smaller number
// because the subsidy sits in the denominator) is returned BESIDE it rather than
// instead of it: they answer different questions and an unlabelled one is a number
// nobody can check against a statement.
function lossRatioOver(row, windowYrs) {
  const yrs = (row && row.years) || [], ind = (row && row.indemnity_by_year) || [];
  if (!yrs.length || yrs.length !== ind.length) return null;
  const want = new Set((windowYrs || yrs).map(Number));
  const hit = [];
  for (let i = 0; i < yrs.length; i++) if (want.has(Number(yrs[i]))) hit.push({ year: Number(yrs[i]), indemnity: Number(ind[i]) || 0 });
  if (!hit.length) return null;
  const pp = Number(row.producer_premium), tp = Number(row.total_premium);
  const totalInd = hit.reduce((a, h) => a + h.indemnity, 0);
  const n = hit.length;
  // A ZERO PREMIUM IS NOT A RATIO. Dividing by it yields Infinity, which formats as
  // a confident-looking figure; null renders as "not available" and says why.
  const ratio = Number.isFinite(pp) && pp > 0 ? totalInd / (pp * n) : null;
  // ── WHAT YOU'D HAVE PAID IN, AND WHAT YOU'D HAVE TAKEN OUT ───────────────
  // Kevin, 2026-09-29: "we need to add total dollars of premium paid and what
  // the total indemnity wouldve been for that time period. If I paid in, how
  // much wouldve I gotten out of it?"
  //
  // It is the ratio's own numerator and denominator, unnormalised — so it is
  // computed HERE, beside the ratio, rather than by each caller. Two callers
  // multiplying premium by years is two chances to disagree about which years
  // the window covered, and the number they would disagree about is the one a
  // grower reads first.
  //
  // ⚠ A NULL PREMIUM GIVES A NULL PAID-IN AND A NULL NET, never a zero. "You
  // would have paid nothing" is a claim; "we cannot say" is the fact.
  const paidIn = Number.isFinite(pp) && pp > 0 ? pp * n : null;
  return {
    basis: "REPLAY_TODAYS_PREMIUM_HISTORIC_INDEX",
    n_years: n, first_year: hit[0].year, last_year: hit[n - 1].year,
    total_indemnity: totalInd,
    avg_indemnity: totalInd / n,
    producer_premium_per_year: Number.isFinite(pp) ? pp : null,
    total_producer_premium: paidIn,
    net: paidIn == null ? null : totalInd - paidIn,
    loss_ratio: ratio,
    loss_ratio_gross: Number.isFinite(tp) && tp > 0 ? totalInd / (tp * n) : null,
    years_paid: hit.filter((h) => h.indemnity > 0).length,
    by_year: hit.map((h) => ({ year: h.year, indemnity: h.indemnity,
      ratio: Number.isFinite(pp) && pp > 0 ? h.indemnity / pp : null })),
  };
}

module.exports = { windowYears, intervalHistory, policyHistory, lossRatioOver, PERIODS, LAB };
